import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  finalize,
  withLatestFrom,
  catchError,
  debounceTime,
  filter,
} from 'rxjs/operators';

import { selectFertilizationSettings } from 'src/app/advice/store/selectors';
import { AidAdvice, CropFieldAdvice, SiteState, Treatment } from 'src/app/shared/models';

import { getCropYear, selectCropFieldAdvices } from 'src/app/store/selectors';

import * as LegalStandardActions from './legal-standard.actions';
import {
  AidAdviceData,
  CropFieldAdviceData,
  FertilizationLegalStandards,
  FertilizationSettingData,
  GenerateFertilizationLegalStandardsRequest,
  TreatmentData,
} from '../models/legal-standard.model';
import { FertilizationLegalStandardsApiService } from '../services/fertilization-legal-standards-api.service';

@Injectable()
export class LegalStandardEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly api = inject(FertilizationLegalStandardsApiService);

  getFertilizationLegalStandards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegalStandardActions.getFertilizationLegalStandards),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectCropFieldAdvices)),
        this.store.pipe(select(selectFertilizationSettings)),
      ),
      filter(
        ([_, __, cropFieldAdvices, fertilizationSettings]) =>
          cropFieldAdvices.length > 0 &&
          fertilizationSettings.filter((f) =>
            cropFieldAdvices.map((a) => a.adviceId).includes(f.adviceId),
          ).length > 0,
      ),
      switchMap(([_, cropYear, cropFieldAdvices, fertilizationSettings]) =>
        cropFieldAdvices.length > 0
          ? this.api
            .getFertilizationLegalStandards(
              this.createFertilizationLegalStandardsRequest(
                cropYear,
                cropFieldAdvices,
                fertilizationSettings.filter((f) =>
                  cropFieldAdvices.map((a) => a.adviceId).includes(f.adviceId),
                ),
              ),
            )
            .pipe(
              catchError((error: HttpErrorResponse) =>
                error.status === 404 ? EMPTY : throwError(error),
              ),
              map((fertilizationLegalStandards) =>
                LegalStandardActions.setFertilizationLegalStandards({
                  fertilizationLegalStandards,
                }),
              ),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              startWith(CoreActions.setLoading({ loading: true })),
            )
          : this.createEmptyFertilizationLegalStandards().pipe(
            map((fertilizationLegalStandards) =>
              LegalStandardActions.setFertilizationLegalStandards({
                fertilizationLegalStandards,
              }),
            ),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            startWith(CoreActions.setLoading({ loading: true })),
          ),
      ),
    ),
  );

  /** Implemented 5 sec delay to work around the fact that
   * some changes are send asynchronous to the getFertilizationLegalStandards endpoint */
  triggerDelayedGetFertilizationLegalStandards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegalStandardActions.triggerDelayedGetFertilizationLegalStandards),
      debounceTime(5000),
      map((_) => LegalStandardActions.getFertilizationLegalStandards()),
    ),
  );

  private createEmptyFertilizationLegalStandards = (): Observable<FertilizationLegalStandards> =>
    of({
      mainCropFields: [],
      subsequentCultivations: [],
      legalStandardBalance: null,
    });

  private createFertilizationLegalStandardsRequest = (
    cropYear: number,
    cropFieldAdvices: CropFieldAdvice[],
    fertilizationSettings: FertilizationSettingData[],
  ): GenerateFertilizationLegalStandardsRequest => ({
    cropYear,
    cropFieldAdvices: Array.from(cropFieldAdvices.map((a) => this.createCropFieldAdviceData(a))),
    fertilizationSettings: Array.from(
      fertilizationSettings.map((f) => this.createFertilizationSettingsData(f)),
    ),
  });

  private createFertilizationSettingsData = (fertilizationSettings: FertilizationSettingData) => {
    const paramsToOmit = [
      'id',
      'customerId',
      'isPrecedingCrop',
      'isGreenManure',
      'isAcceptedByUser',
      'greenManureSettings',
      'precedingCropSettings',
      'organicMatterSupply',
      'expectedCropYield',
    ];
    const fertilizationSettingsData = this.deepClone(fertilizationSettings);
    paramsToOmit.forEach((property) => {
      delete fertilizationSettingsData[property];
    });
    return fertilizationSettingsData;
  };

  private createCropFieldAdviceData = (cropFieldAdvice: CropFieldAdvice): CropFieldAdviceData => {
    const paramsToOmit = [
      'crop',
      'cropVariety',
      'soilType',
      'isSkal',
      'isPlanetProof',
      'isGroundWaterPreservation',
      'isSandySoil',
      'gbmCompleted',
      'fertilizationCompleted',
      'gbmCorrupt',
      'fertilizationCorrupt',
      'cropFieldId',
      'isRentedField',
    ];
    const adviceData = this.deepClone(cropFieldAdvice);
    paramsToOmit.forEach((property) => {
      delete adviceData[property];
    });

    const paramsToOmitFromTreatments = [
      'driftReduction',
      'forbiddenForPlanetProof',
      'isCorrupt',
      'treatmentType',
    ];
    const filteredTreatments: Treatment[] = adviceData.treatments;
    paramsToOmitFromTreatments.forEach((property) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filteredTreatments.forEach((treatment: any) => {
        delete treatment[property];
      });
    });

    return {
      ...adviceData,
      cropId: cropFieldAdvice.crop.id,
      cropName: cropFieldAdvice.crop.name,
      cropVarietyId: cropFieldAdvice.cropVariety?.id,
      cropVarietyName: cropFieldAdvice.cropVariety?.name,
      soilTypeId: cropFieldAdvice.soilType.id,
      treatments: filteredTreatments.map((t) => this.createTreatmentData(t)),
    };
  };

  private createTreatmentData = (treatment: Treatment): TreatmentData => {
    const paramsToOmit = [
      'id',
      'objective',
      'moment',
      'pointOfAttention',
      'shouldUseAreaInHectaresManual',
    ];
    const treatmentData = this.deepClone(treatment);
    paramsToOmit.forEach((property) => {
      delete treatmentData[property];
    });
    return {
      ...treatmentData,
      isCorrupt: treatment.isCorrupt,
      aidAdvices: treatment.aidAdvices.map((a) => this.createAidAdvice(a)),
    };
  };

  private createAidAdvice = (aidAdvice: AidAdvice): AidAdviceData => {
    const paramsToOmit = ['nitrogenEffectivity', 'isActive'];
    const aidAdviceData = this.deepClone(aidAdvice);
    paramsToOmit.forEach((property) => {
      delete aidAdviceData[property];
    });
    return aidAdviceData;
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private deepClone(object: any) {
    return JSON.parse(JSON.stringify(object));
  }
}
